import { computeCalc, dateMs } from './calc';
import type { DateValue, FileValue, PropertyDef, RowData, SelectOption } from './db-types';
import { evaluateFormula, type FValue } from './formula';

/** What value resolution needs to know about the workspace. */
export interface ValueContext {
  schemaOf(dbId: string): PropertyDef[];
  row(id: string): RowData | null;
  dbOf(rowId: string): string | null;
  memberName(id: string): string;
  me: string;
  now(): Date;
}

/**
 * The value of a cell, normalised by property type:
 * text-like → string, number/unique_id → number | null, select/status → option id | null,
 * multi_select/person/relation → string[], date → DateValue | null, checkbox → boolean,
 * files → FileValue[], created/edited time → ms, created/edited by → user id,
 * formula → FValue, rollup → number | string | unknown[] | null.
 */
export function cellValue(ctx: ValueContext, dbId: string, row: RowData, prop: PropertyDef, depth = 0): unknown {
  const raw = row.props[prop.id];
  switch (prop.type) {
    case 'title':
      return row.title;
    case 'text':
    case 'url':
    case 'email':
    case 'phone':
      return typeof raw === 'string' ? raw : '';
    case 'number':
      return typeof raw === 'number' ? raw : null;
    case 'unique_id':
      return typeof raw === 'number' ? raw : null;
    case 'select':
    case 'status': {
      const id = typeof raw === 'string' ? raw : null;
      if (id && prop.options && !prop.options.some((o) => o.id === id)) return null;
      if (!id && prop.type === 'status') return null;
      return id;
    }
    case 'multi_select':
      return Array.isArray(raw) ? (raw as string[]).filter((id) => !prop.options || prop.options.some((o) => o.id === id)) : [];
    case 'person':
    case 'relation':
      return Array.isArray(raw) ? (raw as string[]).filter((id) => prop.type !== 'relation' || !!ctx.row(id)) : [];
    case 'files':
      return Array.isArray(raw) ? (raw as FileValue[]) : [];
    case 'date':
      return raw && typeof raw === 'object' && 'start' in (raw as object) ? (raw as DateValue) : null;
    case 'checkbox':
      return raw === true;
    case 'created_time':
      return row.createdAt;
    case 'last_edited_time':
      return row.editedAt;
    case 'created_by':
      return row.createdBy;
    case 'last_edited_by':
      return row.editedBy;
    case 'button':
      return null;
    case 'formula':
      return depth > 16 ? null : evaluateRowFormula(ctx, dbId, row, prop, depth).value;
    case 'rollup':
      return depth > 16 ? null : rollupValue(ctx, dbId, row, prop, depth);
  }
}

function optionName(prop: PropertyDef, id: string): string {
  return prop.options?.find((o) => o.id === id)?.name ?? '';
}

function toDate(v: DateValue): Date | { kind: 'range'; start: Date; end: Date } {
  const start = new Date(dateMs(v)!);
  if (v.end) {
    const end = new Date(dateMs({ start: v.end, time: v.endTime ?? null })!);
    return { kind: 'range', start, end };
  }
  return start;
}

/** A cell as a formula value (`prop("Name")`). */
export function formulaInput(ctx: ValueContext, dbId: string, row: RowData, prop: PropertyDef, depth = 0): FValue {
  const v = cellValue(ctx, dbId, row, prop, depth + 1);
  switch (prop.type) {
    case 'select':
    case 'status':
      return v ? optionName(prop, v as string) : '';
    case 'multi_select':
      return (v as string[]).map((id) => optionName(prop, id));
    case 'person':
      return (v as string[]).map((id) => ctx.memberName(id));
    case 'created_by':
    case 'last_edited_by':
      return ctx.memberName(v as string);
    case 'relation':
      return (v as string[]).map((id) => ({ kind: 'page' as const, id, title: ctx.row(id)?.title ?? '' }));
    case 'files':
      return (v as FileValue[]).map((f) => f.name);
    case 'date':
      return v ? toDate(v as DateValue) : null;
    case 'created_time':
    case 'last_edited_time':
      return new Date(v as number);
    case 'rollup':
      return Array.isArray(v) ? (v as FValue[]) : (v as FValue);
    default:
      return (v ?? null) as FValue;
  }
}

export function evaluateRowFormula(ctx: ValueContext, dbId: string, row: RowData, prop: PropertyDef, depth = 0) {
  const schema = ctx.schemaOf(dbId);
  return evaluateFormula(prop.expression ?? '', {
    prop: (name) => {
      const p = schema.find((x) => x.name === name);
      if (!p) return undefined;
      if (p.id === prop.id) return null;
      return formulaInput(ctx, dbId, row, p, depth + 1);
    },
    pageProp: (pageId, name) => {
      const other = ctx.row(pageId);
      const otherDb = ctx.dbOf(pageId);
      if (!other || !otherDb) return undefined;
      const p = ctx.schemaOf(otherDb).find((x) => x.name === name);
      return p ? formulaInput(ctx, otherDb, other, p, depth + 1) : undefined;
    },
    now: () => ctx.now(),
  });
}

/** Rollup: aggregate a property of the rows linked through a relation. */
export function rollupValue(ctx: ValueContext, dbId: string, row: RowData, prop: PropertyDef, depth = 0): unknown {
  const cfg = prop.rollup;
  if (!cfg) return null;
  const schema = ctx.schemaOf(dbId);
  const rel = schema.find((p) => p.id === cfg.relationPropId);
  if (!rel?.relation) return null;
  const ids = cellValue(ctx, dbId, row, rel, depth + 1) as string[];
  const target = ctx.schemaOf(rel.relation.dbId).find((p) => p.id === cfg.targetPropId);
  if (!target) return null;
  const values = ids
    .map((id) => ctx.row(id))
    .filter((r): r is RowData => !!r)
    .map((r) => displayValueForRollup(ctx, rel.relation!.dbId, r, target, depth + 1));
  return computeCalc(cfg.calc, values);
}

/** Related values in a shape that both calculations and "Show original" can use. */
function displayValueForRollup(ctx: ValueContext, dbId: string, row: RowData, prop: PropertyDef, depth: number): unknown {
  const v = cellValue(ctx, dbId, row, prop, depth);
  if (prop.type === 'select' || prop.type === 'status') return v ? optionName(prop, v as string) : null;
  if (prop.type === 'multi_select') return (v as string[]).map((id) => optionName(prop, id));
  if (prop.type === 'person') return (v as string[]).map((id) => ctx.memberName(id));
  return v;
}

/** Plain-text rendering of a cell (search, CSV export, group labels). */
export function cellText(ctx: ValueContext, dbId: string, row: RowData, prop: PropertyDef): string {
  const v = cellValue(ctx, dbId, row, prop);
  switch (prop.type) {
    case 'select':
    case 'status':
      return v ? optionName(prop, v as string) : '';
    case 'multi_select':
      return (v as string[]).map((id) => optionName(prop, id)).join(', ');
    case 'person':
      return (v as string[]).map((id) => ctx.memberName(id)).join(', ');
    case 'created_by':
    case 'last_edited_by':
      return ctx.memberName(v as string);
    case 'relation':
      return (v as string[]).map((id) => ctx.row(id)?.title ?? '').join(', ');
    case 'files':
      return (v as FileValue[]).map((f) => f.name).join(', ');
    case 'checkbox':
      return v ? 'Yes' : 'No';
    case 'date': {
      const d = v as DateValue | null;
      if (!d) return '';
      return `${d.start}${d.time ? ` ${d.time}` : ''}${d.end ? ` → ${d.end}${d.endTime ? ` ${d.endTime}` : ''}` : ''}`;
    }
    case 'created_time':
    case 'last_edited_time':
      return new Date(v as number).toISOString();
    case 'unique_id':
      return v === null ? '' : `${prop.prefix ? `${prop.prefix}-` : ''}${v}`;
    case 'number':
      return v === null ? '' : String(v);
    case 'formula': {
      const f = v as FValue;
      if (f instanceof Date) return f.toISOString();
      return f === null ? '' : Array.isArray(f) ? f.map(String).join(', ') : String((f as { title?: string }).title ?? f);
    }
    case 'rollup':
      return Array.isArray(v) ? v.map(String).join(', ') : v === null || v === undefined ? '' : String(v);
    default:
      return String(v ?? '');
  }
}

export function optionById(prop: PropertyDef, id: string | null | undefined): SelectOption | undefined {
  return id ? prop.options?.find((o) => o.id === id) : undefined;
}
